import type { AccessLevel, TierRequired } from "./types";

export const CATALOG_TIME_ZONE = "America/New_York";
export const PLATINUM_AGING_MONTHS = 5;

export interface EpisodeAccessInput {
  release_date?: string;
  tier_required: TierRequired;
}

export interface EffectiveEpisodeAccess {
  originalTierRequired: TierRequired;
  effectiveTierRequired: TierRequired;
  displayTierLabel: string;
  accessBadgeLabel: string;
  isProgressiveCut: boolean;
  isPlatinumEarlyAccess: boolean;
  isAllPaidMembers: boolean;
  platinumUnlocksForAllPaidMembersOn: string | null;
  autoAgingReason: string;
  hasValidReleaseDate: boolean;
}

interface DateParts {
  year: number;
  month: number;
  day: number;
}

function pad(value: number) {
  return String(value).padStart(2, "0");
}

function formatDateOnly(parts: DateParts) {
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
}

function daysInMonth(year: number, month: number) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function parseDateOnly(value?: string | Date | null): DateParts | null {
  if (value instanceof Date) {
    return parseDateOnly(getCurrentCatalogDate(value));
  }
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
}

export function getCurrentCatalogDate(now = new Date(), timeZone = CATALOG_TIME_ZONE) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(now);
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value || 0);
  return formatDateOnly({ year: get("year"), month: get("month"), day: get("day") });
}

export function addCalendarMonths(date: string | Date, months: number) {
  const parts = parseDateOnly(date);
  if (!parts) return null;
  const zeroBased = parts.year * 12 + (parts.month - 1) + months;
  const year = Math.floor(zeroBased / 12);
  const month = ((zeroBased % 12) + 12) % 12 + 1;
  const day = Math.min(parts.day, daysInMonth(year, month));
  return formatDateOnly({ year, month, day });
}

export function getPlatinumAgingCutoff(currentDate: string | Date = getCurrentCatalogDate()) {
  return addCalendarMonths(currentDate, -PLATINUM_AGING_MONTHS);
}

export function isPlatinumAgedOut(publishedDate?: string, currentDate: string | Date = getCurrentCatalogDate()) {
  const published = parseDateOnly(publishedDate);
  const cutoff = getPlatinumAgingCutoff(currentDate);
  if (!published || !cutoff) return false;
  return formatDateOnly(published) <= cutoff;
}

export function canAccessTier(required: TierRequired, accessLevel: AccessLevel) {
  if (required === "free") return accessLevel !== "visitor";
  if (required === "paid") return accessLevel === "paid_patron" || accessLevel === "platinum_patron";
  return accessLevel === "platinum_patron";
}

export function getEffectiveEpisodeAccess(
  episode: EpisodeAccessInput,
  currentDate: string | Date = getCurrentCatalogDate()
): EffectiveEpisodeAccess {
  const originalTierRequired = episode.tier_required;
  const validReleaseDate = Boolean(parseDateOnly(episode.release_date));

  if (originalTierRequired === "paid") {
    return {
      originalTierRequired,
      effectiveTierRequired: "paid",
      displayTierLabel: "Members+ Progressive Cuts",
      accessBadgeLabel: "Progressive Cut",
      isProgressiveCut: true,
      isPlatinumEarlyAccess: false,
      isAllPaidMembers: false,
      platinumUnlocksForAllPaidMembersOn: null,
      autoAgingReason: "Members+ videos are presented as Progressive Cuts.",
      hasValidReleaseDate: validReleaseDate
    };
  }

  if (originalTierRequired === "platinum") {
    const unlockDate = validReleaseDate ? addCalendarMonths(episode.release_date || "", PLATINUM_AGING_MONTHS) : null;
    const aged = isPlatinumAgedOut(episode.release_date, currentDate);
    if (aged) {
      return {
        originalTierRequired,
        effectiveTierRequired: "paid",
        displayTierLabel: "All Paid Members",
        accessBadgeLabel: "All Paid Members",
        isProgressiveCut: false,
        isPlatinumEarlyAccess: false,
        isAllPaidMembers: true,
        platinumUnlocksForAllPaidMembersOn: unlockDate,
        autoAgingReason: "Released from Platinum Early Access after 5 months.",
        hasValidReleaseDate: validReleaseDate
      };
    }
    return {
      originalTierRequired,
      effectiveTierRequired: "platinum",
      displayTierLabel: "Platinum Early Access",
      accessBadgeLabel: "Platinum Early Access",
      isProgressiveCut: false,
      isPlatinumEarlyAccess: true,
      isAllPaidMembers: false,
      platinumUnlocksForAllPaidMembersOn: unlockDate,
      autoAgingReason: validReleaseDate ? "Moves to All Paid Members on the computed 5-month unlock date." : "Missing or invalid release date; automatic aging is disabled.",
      hasValidReleaseDate: validReleaseDate
    };
  }

  return {
    originalTierRequired,
    effectiveTierRequired: "free",
    displayTierLabel: "Free Preview",
    accessBadgeLabel: "Free Preview",
    isProgressiveCut: false,
    isPlatinumEarlyAccess: false,
    isAllPaidMembers: false,
    platinumUnlocksForAllPaidMembersOn: null,
    autoAgingReason: "Public preview page; no private destination is exposed.",
    hasValidReleaseDate: validReleaseDate
  };
}

export function getTierAgingDiagnostics(episodes: EpisodeAccessInput[], currentDate: string | Date = getCurrentCatalogDate()) {
  const catalogDate = typeof currentDate === "string" ? currentDate : getCurrentCatalogDate(currentDate);
  const platinumAgingCutoff = getPlatinumAgingCutoff(catalogDate) || "";
  const enriched = episodes.map((episode) => ({ episode, effective: getEffectiveEpisodeAccess(episode, catalogDate) }));
  return {
    currentCatalogDate: catalogDate,
    platinumAgingCutoff,
    progressiveCuts: enriched.filter((item) => item.effective.isProgressiveCut).length,
    freshPlatinumEarlyAccess: enriched.filter((item) => item.effective.isPlatinumEarlyAccess).length,
    allPaidMembers: enriched.filter((item) => item.effective.isAllPaidMembers).length,
    missingReleaseDates: enriched.filter((item) => !item.effective.hasValidReleaseDate).length
  };
}
